<template>
  <div class="row q-col-gutter-xs justify-center">
    <template
      v-if="showBackupBanner"
    >
      <banner
        v-if="showBackupNotes"
        type="error"
        variant="multi-line"
        :message="$t('sessions.sessionCard.backupNotesCopyMessage')"
        class="q-mb-md"
      >
        <template #action>
          <q-btn
            flat
            :label="$t('sessions.sessionCard.btnViewRemoteNotesLabel')"
            @click="onSwitchNotesView"
          />
          <q-btn
            flat
            :label="$t('sessions.sessionCard.btnSaveBackupNotesLabel')"
            @click="onRestoreBackupNotes"
          />
        </template>
      </banner>
      <banner
        v-else
        type="error"
        variant="multi-line"
        :message="$t('sessions.sessionCard.localNotesCopyMessage')"
        class="q-mb-md"
      >
        <template #action>
          <q-btn
            flat
            :label="$t('sessions.sessionCard.btnViewNotesLabel')"
            @click="onSwitchNotesView"
          />
          <q-btn
            flat
            :label="$t('common.buttonDismiss')"
            @click="onDismiss"
          />
        </template>
      </banner>
    </template>
    <banner
      v-if="addendumsStatus"
      :type="isAddendumPending ? 'warning' : 'info'"
      show-icon
      class="text-left q-mb-md"
      :message="$t(`addendums.banner.${addendumsStatus}`)"
    >
      <template #action>
        <q-btn
          flat
          :label="$t('addendums.banner.action')"
          @click="scrollToAddendums"
        />
      </template>
    </banner>
    <div
      v-if="showSessionNoteTemplateSelect"
      class="col-12"
    >
      <q-select
        v-if="canChangeNoteTemplate"
        :model-value="existingSessionNoteTemplateId"
        :options="availableSessionNoteTemplates"
        :label="getSessionActivityNoteTemplateLabel(sessionActivity)"
        option-value="id"
        option-label="noteTemplateName"
        clearable
        outlined
        :disable="disableSelectNoteTemplate"
        emit-value
        map-options
        hide-bottom-space
        :hint="noteRequiredHint"
        :loading="isLoadingTemplates"
        class="col-12 q-mb-none"
        data-e2e="dropdown-note-templates"
        @update:model-value="onSelectNoteTemplate"
        @filter="loadAllTemplates"
      >
        <template #option="scope">
          <q-item
            v-bind="scope.itemProps"
            :data-e2e="`dropdown-item-note-template-${scope.opt.noteTemplateName}`"
          >
            <q-item-section>
              <q-item-label>{{ scope.opt.noteTemplateName }}</q-item-label>
              <q-item-label
                v-if="offlineMode && !scope.opt.isAvailableOffline"
                caption
              >
                {{ $t('sessions.noteTemplate.staffMemberFieldNotAvailable') }}
              </q-item-label>
            </q-item-section>
            <q-item-section
              v-if="offlineMode && !scope.opt.isAvailableOffline"
              avatar
            >
              <q-icon :name="'warning'" />
            </q-item-section>
          </q-item>
        </template>
        <template #no-option>
          <q-item>
            <q-item-section class="text-grey">
              {{ $t('editProgram.itemNoTemplates') }}
            </q-item-section>
          </q-item>
        </template>
      </q-select>
    </div>
    <div
      v-else-if="isLoadingTemplates"
      class="row col-12 justify-center items-center"
    >
      <q-spinner class="q-mr-md" /> {{ $t('sessions.sessionCard.loadingSessionNoteTemplates') }}
    </div>
    <div
      v-if="canCompleteSessionNoteTemplate"
      class="col-12"
    >
      <universal-tooltip
        :show-tooltip="!!completeSessionNoteTemplateTooltip"
        :text="completeSessionNoteTemplateTooltip"
      >
        <q-btn
          :label="getSessionActivityNotesButtonLabel(sessionActivity)"
          :disable="!isNotesEditable || !canChangeSessionNotes"
          :loading="isFinishingSessionActivity || isNotesSaving"
          icon="launch"
          :color="isBackupDifferent && (session !== null) && !offlineMode ? 'negative' : 'primary'"
          class="q-mt-md full-width"
          @click="onCompleteSessionNoteTemplate"
        />
      </universal-tooltip>
    </div>
    <template
      v-else
    >
      <div class="col-12">
        <div
          v-if="isSessionInProgress"
          class="wrapper"
        >
          <p
            v-if="!isMobileView"
            class="autosave-status"
            :class="{'autosave-status--saving': isSessionNotesChanged}"
          >
            {{
              isSessionNotesChanged
                ? $t('sessions.sessionCard.unsavedChanges')
                : $t('sessions.sessionCard.savedChanges')
            }}
          </p>
          <q-icon
            class="autosave-icon"
            :class="{'autosave-icon--saving': isSessionNotesChanged}"
            :name="isSessionNotesChanged ? 'save_as_outlined' : 'save_outlined'"
            size="sm"
          />
        </div>
        <banner
          v-if="showRequestBanner"
          type="warning"
          show-icon
          class="text-left q-my-md"
          :message="sessionActivity.changeRequest.status === 'rejected' ?
            $t('sessions.sessionCard.changeRequestBanner.titleAfterRejection') :
            $t('sessions.sessionCard.changeRequestBanner.title')"
        >
          <template #action>
            <q-btn
              flat
              :label="$t('sessions.sessionCard.changeRequestBanner.bannerBtn')"
              @click="scrollToRequestBanner"
            />
          </template>
        </banner>
        <text-editor
          v-show="showTextEditor"
          ref="editor"
          v-model="editNotes"
          :disabled="!isNotesEditable || !canChangeSessionNotes || isContentLocked"
          :loading="isEditorLoadingOrNotesSaving"
          tables
          indent
          image-upload
          enable-dynamic-fields
          checklist
          wrapper-class="q-mt-xs"
          data-e2e="input-notes"
          @blur="onAutoSaveDraft"
          @new-image="onAutoSaveWithLoader"
          @image-deleted="onAutoSaveWithLoader"
          @update:model-value="debouncedAutoSave"
        />
        <banner
          v-if="noteValidationError"
          type="error"
          show-icon
          :message="$t('sessions.noteTemplate.validateSessionNoteFieldEmpty')"
          class="q-mt-sm"
        />
      </div>
      <div
        ref="changeRequestBanner"
        class="full-width"
      >
        <change-request-banner
          v-if="showRequestBanner"
          :original-session-activity="sessionActivity"
          :requested-changes="requestedChanges"
          :reason="sessionActivity.changeRequest?.reason"
          @approve-request="onApproveChangeRequest"
          @reject-request="onRejectChangeRequest"
          @delete-reject="onDeleteRejectChangeRequest"
          @create-request="onCreateChangeRequest"
        />
      </div>

      <div class="col-12">
        <div
          v-if="showUnrenderedActivityWarning"
          class="q-py-md"
        >
          <banner
            type="warning"
            :message="$t('signature.unrenderedActivityWarning')"
          />
        </div>
        <session-activity-signatures
          :signatures="signatures"
          :required-signatories="requiredSignatories"
          :is-activity-rendered="!!sessionActivity.renderedTimestamp"
          @sign-request="onSignRequest"
          @delete-signature="onDeleteSignature"
          @cancel-signature-request="onCancelSignatureRequest"
        />
      </div>
      <div class="col-12">
        <div class="row q-col-gutter-sm">
          <template v-if="canManageSignatures">
            <q-space />
            <div
              v-if="canRequestSignature"
            >
              <q-btn
                flat
                color="primary"
                icon="person_add_alt"
                :label="$t('signature.btnRequestSignature')"
                data-e2e="button-request-signature"
                @click="onRequestSignature"
              />
            </div>
            <div
              v-if="isSignedByUser"
            >
              <q-btn
                color="primary"
                icon="add"
                :label="$t('signature.btnAddSignature')"
                :loading="sessionSignatureLoadingStatus.signing"
                @click="onAddSignature"
              />
            </div>
            <div
              v-if="!hasUserSignatureRequest && !isSignedByUser"
            >
              <q-btn
                color="primary"
                icon="fas fa-file-signature"
                :label="$q.screen.gt.sm ? $t('signature.btnSignNow') : null"
                data-e2e="button-sign-now"
                @click="onSignNow"
              />
            </div>
          </template>
          <template v-if="isNotesEditable && !autoSave && showTextEditor">
            <q-space />
            <universal-tooltip
              :text="$t('sessions.sessionCard.disabledEditingSessionNotes')"
              :show-tooltip="!canChangeSessionNotes"
            >
              <div class="flex">
                <div>
                  <q-btn
                    color="secondary"
                    flat
                    :disable="isDisabledCancelButton"
                    :label="$t('common.buttonCancel')"
                    @click="onCancelChanges"
                  />
                </div>
                <div>
                  <q-btn
                    color="primary"
                    flat
                    :disable="isDisabledSaveDraftButton"
                    :label="$t('common.buttonSaveDraft')"
                    :loading="isSavingDraft"
                    data-e2e="button-save"
                    @click="onSaveDraft"
                  />
                </div>
                <universal-tooltip
                  :show-tooltip="isOffline"
                  :text="$t('common.noInternetConnection')"
                >
                  <div>
                    <q-btn
                      color="primary"
                      :disable="isDisabledSaveButton"
                      :label="saveButtonLabel"
                      :loading="isSaving"
                      @click="onSaveChanges"
                    />
                  </div>
                </universal-tooltip>
              </div>
            </universal-tooltip>
          </template>
        </div>
      </div>
      <div
        v-if="addendumsLoadError"
        class="col-12"
      >
        <banner
          type="error"
          show-icon
          class="text-left q-mt-md"
          :message="$t('addendums.errors.loadFailed')"
        />
      </div>
      <div
        v-else-if="showAddendumsSkeleton"
        class="col-12"
      >
        <hr-legacy-skeleton
          type="rect"
          height="120px"
          class="q-mt-md"
        />
      </div>
      <div
        v-else-if="addendums.length"
        ref="addendumCards"
        class="col-12"
      >
        <session-activity-addendum
          v-for="addendum in addendums"
          :key="addendum.id"
          :addendum="addendum"
          :session-activity="sessionActivity"
          :disable-actions="isOffline || offlineMode"
          class="q-mt-md"
          @request-signature="onAddendumRequestSignature(addendum)"
          @add-signature="onAddendumAddSignature(addendum)"
          @sign-now="onAddendumSignNow(addendum)"
          @sign-request="onAddendumSignRequest(addendum, $event)"
          @cancel-signature-request="onAddendumCancelSignatureRequest(addendum, $event)"
          @delete="onAddendumDelete(addendum)"
        />
      </div>
    </template>
  </div>
</template>
<script>
import ChangeRequestBanner from 'components/ChangeRequest/ChangeRequestBanner';
import ChangeRequestRejectModal from 'components/ChangeRequest/ChangeRequestRejectModal';
import ChangeSessionActivityAppointment from 'components/SessionActivities/Modals/ChangeSessionActivityAppointment';
import SessionActivityAddendum from 'components/SessionActivities/SessionActivityAddendum';
import SessionActivitySignatures from 'components/SessionActivities/SessionActivitySignatures';
import SessionActivityChangeRequest from 'components/Sessions/Modals/SessionActivityChangeRequest';
import RequestSignature from 'components/Sessions/RequestSignature';
import SessionNoteFieldPrompt from 'components/Sessions/SessionNoteTemplates/SessionNoteFieldPrompt';
import Signature from 'components/Signature';
import Banner from 'components/UI/Banner';
import TextEditor from 'components/UI/TextEditor';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import { Timestamp } from 'firebase/firestore';
import {
  chain, debounce, filter, find, get, includes, isDate, isEmpty, isEqual, isFunction, merge, size,
  sortBy,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { uid } from 'quasar';
// The barrel does not export HrLegacySkeleton; its replacement (a component's `pending` state) is not
// available for this placeholder yet.
// eslint-disable-next-line import/no-restricted-paths
import { HrLegacySkeleton } from 'src/design-system/migration';
import { useAddendums } from 'src/composables/addendums';
import { useAppointments } from 'src/composables/appointments';
import { useClients } from 'src/composables/clients';
import { useSessionActivityChangeRequest } from 'src/composables/sessionActivityChangeRequest';
import { useSignature } from 'src/composables/signature';
import { ADDENDUM_STATUSES } from 'src/constants/sessionActivities';
import ClientsDB from 'src/fb/clients-db';
import SessionNoteTemplatesDB from 'src/fb/sessionNoteTemplates-db';
import Activities from 'src/mixins/Activities';
import Auth from 'src/mixins/Auth';
import Interactions from 'src/mixins/Interactions';
import SessionActivitiesMixin from 'src/mixins/SessionActivitiesMixin';
import SessionNotesTemplates from 'src/mixins/SessionNotesTemplates';
import TemplateMixin from 'src/mixins/TemplateMixin';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { dataFields } from 'src/store/dataFields';
import { geolocation } from 'src/store/geolocation';
import { network as networkStore } from 'src/store/network';
import { units } from 'src/store/units';
import AppUtils from 'src/utils/AppUtils';
import { logEvent } from 'src/utils/LogEvent';
import { getCurrentInstance, toRef } from 'vue';
import {
  replaceLocalImagesWithStorage,
  replaceStorageImagesWithLocal,
} from 'src/utils/OfflineAccessUtils';

export default {
  name: 'SessionActivityNotesEditor',

  components: {
    SessionActivityAddendum,
    SessionActivitySignatures,
    TextEditor,
    UniversalTooltip,
    ChangeRequestBanner,
    Banner,
    HrLegacySkeleton,
  },

  mixins: [
    Activities,
    Auth, Interactions,
    SessionActivitiesMixin,
    SessionNotesTemplates,
    TemplateMixin,
    Toolbox,
  ],

  async beforeRouteLeave(to, from, next) {
    await Promise.all([
      this.unbindBackupsData(),
      this.unbindBackupNotes(),
    ]);
    next();
  },

  props: {
    sessionActivity: {
      type: Object,
      required: true,
    },

    session: {
      type: Object,
      default: null,
    },

    linkedAppointment: {
      type: Object,
      default: null,
    },

    allPrograms: {
      type: Array,
      default: () => ([]),
    },

    client: {
      type: Object,
      default: null,
    },

    label: {
      type: String,
      default: null,
    },

    autoSave: {
      type: Boolean,
      default: false,
    },

    applyImmediately: {
      type: Boolean,
      default: false,
    },

    isBackupDifferent: {
      type: Boolean,
      default: false,
    },

  },

  emits: [
    'changed',
    'delete-signature',
    'cancel-signature-request',
    'request-signature',
    'add-signature',
    'save',
    'has-note-template',
    'show-backup-notes',
    'scroll-to-banner',
  ],

  setup(props) {
    const vm = getCurrentInstance();
    const { mapChangeRequestData, getFieldDataForApproval } = useSessionActivityChangeRequest();
    const { getStaffInfoForSignature } = useSignature();
    const { getSessionClient } = useClients();
    const {
      appointmentLookupStartDate, loadLinkableAppointments, linkAppointmentToSessionActivity,
    } = useAppointments();
    const {
      addendums,
      isLoadingAddendums,
      addendumsLoadError,
      requestSignature,
      signRequest,
      signNow,
      addSignature,
      cancelSignatureRequest,
      deleteAddendum,
    } = useAddendums({
      sessionActivity: toRef(props, 'sessionActivity'),
      session: toRef(props, 'session'),
      client: toRef(props, 'client'),
      // Read off the instance: a computed and a mixin method, which setup can't see.
      getSessionNoteTemplate: () => vm.proxy.sessionNoteTemplate,
      uploadSignature: (...args) => vm.proxy.uploadSignature(...args),
    });

    return {
      mapChangeRequestData,
      getFieldDataForApproval,
      getStaffInfoForSignature,
      getSessionClient,
      appointmentLookupStartDate,
      loadLinkableAppointments,
      linkAppointmentToSessionActivity,
      addendums,
      isLoadingAddendums,
      addendumsLoadError,
      onAddendumRequestSignature: requestSignature,
      onAddendumSignRequest: signRequest,
      onAddendumSignNow: signNow,
      onAddendumAddSignature: addSignature,
      onAddendumCancelSignatureRequest: cancelSignatureRequest,
      onAddendumDelete: deleteAddendum,
    };
  },

  data() {
    return {
      isLoadingTemplates: false,
      isSaving: false,
      isSavingDraft: false,
      showBackupNotes: false,
      editorLoader: false,
      alwaysShowNotesEditor: false,

      editNotes: '',
      offlineSessionActivityNotes: '',
      sessionNoteTemplates: [],
      existingSessionNoteTemplate: null,
      sessionNoteFieldData: null,
      debouncedAutoSave: null,

      sessionNoteFieldPrompt: null,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'connectedClinic', 'useLegalNameInSignature',
      'activityTypesSettings', 'isParent']),
    ...mapState(networkStore, ['networkOnline', 'offlineMode']),
    ...mapState(dataFields, ['clientDataFields', 'staffProfile', 'staffMemberDataFields']),

    isOffline() {
      return !this.networkOnline && !this.offlineMode;
    },

    disableSelectNoteTemplate() {
      return !this.isNotesEditable || this.isOffline || this.isEditorLoadingOrNotesSaving;
    },

    canManageSignatures() {
      return ['pending-signatures', 'closed'].includes(this.sessionActivity.status)
        && !this.isParent;
    },

    canRequestSignature() {
      return !isEmpty(this.client);
    },

    hasUserSignatureRequest() {
      return !(chain(this.requiredSignatories)
        .find({ userId: this.userDetails.userId })
        .isEmpty()
        .value());
    },

    isNotesSaving() {
      return this.isSaving || this.isSavingDraft;
    },

    isEditorLoadingOrNotesSaving() {
      return this.editorLoader || (!this.autoSave && this.isNotesSaving);
    },

    isNotesChanged() {
      return this.editNotes !== get(this.sessionActivity, 'notes', '');
    },

    isSessionInProgress() {
      return (get(this.sessionActivity, 'status') === 'in-progress');
    },

    isFinishingSessionActivity() {
      return this.applyImmediately && this.isSessionInProgress;
    },

    isSignedByUser() {
      return !(chain(this.signatures)
        .find({ userId: this.userDetails.userId })
        .isEmpty()
        .value());
    },

    isSessionNotesChanged() {
      if (this.offlineMode) {
        this.$emit('changed', this.editNotes !== this.offlineSessionActivityNotes);
        return this.editNotes !== this.offlineSessionActivityNotes;
      }

      this.$emit('changed', this.editNotes !== this.sessionActivity.notes);
      return this.editNotes !== this.sessionActivity.notes;
    },

    canCloseNotes() {
      return ['finished'].includes(this.sessionActivity.status)
        && (this.editNotes !== '')
        && !this.isOffline;
    },

    isCurrentSessionOwner() {
      return this.userDetails.userId === get(this.session, 'runBy');
    },

    isSessionActivityUser() {
      return this.userDetails.userId === this.sessionActivity.user;
    },

    isNotesEditable() {
      return (
        (
          ['in-progress'].includes(this.sessionActivity.status)
          && (this.isSessionActivityUser || this.isCurrentSessionOwner)
        )
        || ['finished'].includes(this.sessionActivity.status)
      );
    },

    canChangeNoteTemplate() {
      return !this.sessionNoteTemplateId
        || this.userHasPrivilege('sessions/changeSessionNoteTemplate');
    },

    signatures() {
      return get(this.sessionActivity, 'signatures', []);
    },

    requiredSignatories() {
      return get(this.sessionActivity, 'requiredSignatories', []);
    },

    // Gated on the activity's own flag, so notes without an addendum - which is
    // most of them - don't flash a placeholder for something that never arrives.
    showAddendumsSkeleton() {
      return this.sessionActivity.hasAddendum && this.isLoadingAddendums && !this.addendums.length;
    },

    // Same rule as deriveActivityAddendumStatus in functions/utils/addendums.js -
    // keep both in sync.
    addendumsStatus() {
      if (!this.addendums.length) {
        return null;
      }

      return this.addendums.some(({ status }) => status === ADDENDUM_STATUSES.PENDING_SIGNATURES)
        ? ADDENDUM_STATUSES.PENDING_SIGNATURES
        : ADDENDUM_STATUSES.COMPLETE;
    },

    isAddendumPending() {
      return this.addendumsStatus === ADDENDUM_STATUSES.PENDING_SIGNATURES;
    },

    sessionNoteTemplateId() {
      return get(this.sessionActivity, 'sessionNoteTemplateId', null);
    },

    availableSessionNoteTemplates() {
      return this.naturalSort(
        filter(this.sessionNoteTemplates, (sessionNoteTemplate) => {
        // Make sure we always show the existing template - even if activity type has changed
          if (sessionNoteTemplate.id === this.sessionNoteTemplateId) {
            return true;
          }
          if (!isEmpty(sessionNoteTemplate.sessionActivityTypes)) {
            return includes(sessionNoteTemplate.sessionActivityTypes, this.sessionActivity.type);
          }
          return true;
        }),
        'noteTemplateName',
      );
    }, // availableSessionNoteTemplates()

    hasSessionNoteTemplates() {
      return !isEmpty(this.availableSessionNoteTemplates);
    }, // hasSessionNoteTemplates()

    sessionNoteTemplate() {
      return find(this.sessionNoteTemplates, { id: this.sessionNoteTemplateId });
    }, // sessionNoteTemplate()

    isContentLocked() {
      return this.sessionNoteTemplate?.lockContent || false;
    },

    /**
     * Get the first note data field which has a difference from the activity corresponding field
     * @returns {string | null} - id of the field
     */
    activityNoteFieldWithDifference() {
      const notesFromBackup = this.getBackupNotes(this.sessionActivity.id, true);
      const notesFields = this.getSessionNotesFieldDataFromSessionActivity(this.sessionActivity);

      if (!notesFromBackup) {
        return null;
      }

      if (notesFromBackup.notes) {
        return '_done';
      }

      const isTimestamp = (obj) => {
        return obj instanceof Timestamp;
      };

      return chain(notesFromBackup.fieldsData)
        .map((value, key) => {
          if (!isEqual(notesFields[key], value)) {
            // If value is an object and not an instance of Timestamp, then it's a group of fields.
            // Therefore, the string ${key}.${name of the first field in the group of fields} should be returned.

            if (AppUtils.isPlainObject(value) && AppUtils.isPlainObject(notesFields[key]) && !isTimestamp(value)
                && !isTimestamp(notesFields[key]) && !isDate(value) && !isDate(notesFields[key])) {
              return `${key}.${Object.keys(value)[0]}`;
            }
            return key;
          }

          return null;
        })
        .compact()
        .head()
        .value();
    }, // activityNoteFieldWithDifference()

    isOnlyStringSessionNotesBackup() {
      if (this.session === null) {
        return;
      }

      const notesFromBackup = this.getBackupNotes(this.sessionActivity.id);

      return (notesFromBackup?.notes && !notesFromBackup?.fieldsData);
    }, // isOnlyStringSessionNotesBackup()

    existingSessionNoteTemplateId() {
      return this.sessionNoteTemplate
        ? this.sessionNoteTemplateId
        : '';
    }, // existingSessionNoteTemplateId()

    saveButtonLabel() {
      // Handle signatures, signature requests from template
      const signatureRequired = get(this.sessionNoteTemplate, 'requireSignature', false) && !this.isSignedByUser;
      if (signatureRequired) {
        return this.$t('common.buttonSignAndSave');
      }
      return this.$t('common.buttonSave');
    },

    completeSessionNoteTemplateTooltip() {
      if (this.isBackupDifferent && this.session && this.canChangeSessionNotes && !this.offlineMode) {
        return this.$t('run.collectData.btnSessionNotesBackupHasDifference');
      }

      if (!this.canChangeSessionNotes) {
        return this.$t('sessions.sessionCard.disabledEditingSessionNotes');
      }

      return '';
    },

    showBackupBanner() {
      return this.session && this.isBackupDifferent && this.isOnlyStringSessionNotesBackup
          && !this.offlineMode && this.canChangeSessionNotes;
    },

    showSessionNoteTemplateSelect() {
      return this.canChangeSessionNotes
        && ((this.hasSessionNoteTemplates || this.sessionNoteTemplateId || this.isNoteRequired)
          && this.isNotesEditable);
    },

    canCompleteSessionNoteTemplate() {
      return (['in-progress', 'finished'].includes(this.sessionActivity?.status)
        && this.sessionNoteTemplateId)
        && !(
          ['in-progress'].includes(this.sessionActivity?.status)
          && this.alwaysShowNotesEditor
          && this.sessionActivity?.notes
        );
    },

    isDisabledCancelButton() {
      return !this.isSessionNotesChanged || this.isNotesSaving || !this.canChangeSessionNotes;
    },

    isDisabledSaveDraftButton() {
      return !this.isSessionNotesChanged || this.isSaving || !this.canChangeSessionNotes;
    },

    isDisabledSaveButton() {
      return !this.canCloseNotes || this.isSavingDraft || !this.canChangeSessionNotes;
    },

    isAlohaIntegration() {
      return this.connectedClinic.integration === 'aloha';
    },

    isUnrenderedAppointment() {
      return this.linkedAppointment?.isUnrendered;
    },

    showUnrenderedActivityWarning() {
      return this.canManageSignatures
        && this.isAlohaIntegration
        && this.isUnrenderedAppointment
        && !this.signatures.length;
    },

    isMobileView() {
      return this.$q.platform.is.mobile || this.$q.screen.width <= 730;
    }, // isMobileView()

    requestedChanges() {
      return this.sessionActivity.changeRequest?.fields?.reduce((acc, current) => {
        return Object.assign(acc, current);
      }, {});
    },

    isApprovedRequestedChanges() {
      return this.sessionActivity.changeRequest?.status === 'approved';
    },

    showRequestBanner() {
      return this.sessionActivity.changeRequest?.fields && !this.isApprovedRequestedChanges;
    },

    sessionNoteFields() {
      const sessionNotesFieldDataObject = this.sessionActivity.sessionNotesFieldData.session;

      return Object.entries(sessionNotesFieldDataObject).map(([key, value]) => {
        if (key === 'sessionStartTime' || key === 'sessionFinishTime') {
          const { seconds } = value;
          const newDate = new Date(seconds * 1000);
          return {
            id: key,
            value: newDate,
          };
        }
        return {
          id: key,
          value,
        };
      });
    },

    isNoteRequired() {
      return !!this.activityTypesSettings[this.sessionActivity.type]?.noteRequired;
    },

    showTextEditor() {
      if (this.isNoteRequired) {
        return !this.sessionNoteFieldPrompt
          && !!(this.sessionNoteTemplateId || this.sessionActivity.notes)
          && !!this.editNotes;
      }
      return true;
    },

    noteRequiredHint() {
      return !this.sessionNoteTemplateId && this.isNoteRequired
        ? this.$t('sessions.modals.sessionActivity.inputNoteTemplateLabels.noteRequired') : '';
    },
  },

  watch: {
    editNotes() {
      if (this.noteValidationError) {
        this.noteValidationError = false;
      }
    },

    'sessionActivity.notes': {
      immediate: true,
      async handler(notes) {
        if (this.offlineMode) {
          const offlineNotes = await replaceStorageImagesWithLocal(notes);

          this.offlineSessionActivityNotes = offlineNotes;
          this.editNotes = offlineNotes;
        } else {
          this.editNotes = notes;
        }
        this.$emit('changed', false);

        // Make sure we update non-reactive properties for the editor
        const templateText = get(this.sessionNoteTemplate, 'templateText', '');
        const templateFields = this.fetchTemplateFields(templateText);
        await this.updateSessionNoteFieldData(templateFields);
      },
    },

    client: {
      handler(newVal, oldVal) {
        if ((newVal && !oldVal) || (newVal?.id !== oldVal?.id)) {
          // Make sure we update non-reactive properties for the editor
          const templateText = get(this.sessionNoteTemplate, 'templateText', '');
          const templateFields = this.fetchTemplateFields(templateText);
          this.updateSessionNoteFieldData(templateFields);
        }
      },
    },

    'sessionActivity.status': {
      immediate: true,
      handler(status, prevStatus) {
        if (!this.applyImmediately || (this.isLoadingTemplates)) {
          return;
        }
        if (this.sessionNoteTemplateId
          && (status === 'finished') && (prevStatus === 'in-progress')
          && (!get(this.sessionActivity, 'notes'))) {
          this.applySessionNoteTemplate(
            this.sessionNoteTemplate,
            get(this.sessionActivity, 'sessionNotesCurrentField', null),
            get(this.sessionActivity, 'notes', ''),
          );
        }
      },
    },

  },

  async created() {
    await Promise.all([
      this.bindBackupsData(this.sessionActivity.session),
      this.bindBackupNotes(this.sessionActivity.session),
    ]);
    this.sessionNoteFieldPrompt = null;

    if (this.autoSave) {
      this.debouncedAutoSave = debounce(this.onChangedNotesAutoSave, 3000);
    }
  },

  mounted() {
    this.loadCurrentSessionNoteTemplate();
  },

  methods: {
    ...mapActions(geolocation, ['refreshGeolocation']),
    ...mapActions(units, ['loadClinicUnits']),
    ...mapActions(clients, ['getStaffMemberByUser']),

    async onChangedNotesAutoSave() {
      if (this.isSessionNotesChanged) {
        await this.onAutoSaveDraft();
      }
    }, // onChangedNotesAutoSave

    async updateSessionNoteFieldData(templateFields = [], appointmentOverride = null) {
      let staffMember = null;
      if (
        !this.offlineMode
        && this.sessionActivity?.user
        && (this.templateFieldsHasGroup(templateFields, 'staffMember'))
      ) {
        // Because we might need to load the staff member from Firestore we only
        // do that if the templateFields utilizes the staffMember (to preserve performance)
        this.$q.loading.show();
        try {
          staffMember = await this.getStaffMemberByUser(this.sessionActivity?.user);
        } catch (err) {
          this.notifyNegative(this.$t('common.accessDenied'));
          logEvent(err);
        } finally {
          this.$q.loading.hide();
        }
      }

      let sessionNoteFieldData = this.compileSessionNoteInformation(
        this.session,
        this.client,
        this.sessionActivity,
        appointmentOverride || this.linkedAppointment,
        staffMember,
      );
      const existingFieldData = this.getSessionNotesFieldDataFromSessionActivity(this.sessionActivity);

      if (!isEmpty(existingFieldData)) {
        // Re-use existing sessionNotesFieldData
        sessionNoteFieldData = merge({}, existingFieldData, sessionNoteFieldData);
      }

      this.sessionNoteFieldData = sessionNoteFieldData;
      this.updateSessionNoteFieldPromptParams({ sessionNoteFieldData: this.sessionNoteFieldData });
    }, // updateSessionNoteFieldData()

    isDeleteImageButtonClicked(event) {
      return get(event, ['relatedTarget', 'attributes', 'data-delete-img'], false);
    }, // isDeleteImageButtonClicked()

    async onSignNow() {
      this.$q.loading.show();
      const staffInfo = await this.getStaffInfoForSignature(
        this.userDetails.name,
        this.userDetails.userId,
        this.sessionNoteTemplate?.includeProfessionalCredentials,
      );
      this.$q.loading.hide();

      this.showSignatureDialog(false, this.userDetails.userId, staffInfo);
    }, // onSignNow()

    onAddSignature() {
      this.showSignatureDialog(true, null, { userName: null });
    }, // onAddSignature()

    onRequestSignature() {
      this.showRequestSignatureDialog();
    }, // onRequestSignature()

    async onSignRequest(signatory) {
      this.$q.loading.show();
      const staffInfo = await this.getStaffInfoForSignature(
        signatory.userName,
        signatory.userId,
        this.sessionNoteTemplate?.includeProfessionalCredentials,
      );
      this.$q.loading.hide();

      this.showSignatureDialog(
        signatory.userId === null,
        signatory.userId,
        staffInfo,
        signatory.label,
        signatory.clientDataField,
      );
    }, // onSignRequest()

    onDeleteSignature(signature) {
      this.$emit('delete-signature', signature);
    }, // onDeleteSignature()

    onCancelSignatureRequest(requiredSignatory) {
      this.$emit('cancel-signature-request', requiredSignatory);
    }, // onCancelSignatureRequest()

    onCancelChanges() {
      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t('common.notifyCancelChangesConfirm'),
        () => {
          if (this.offlineMode) {
            this.editNotes = this.offlineSessionActivityNotes;
            return;
          }

          this.editNotes = get(this.sessionActivity, 'notes', '');
        },
      );
    }, // onCancelChanges()

    /** Show loader disabling user interaction with editor and save the session notes */
    async onAutoSaveWithLoader() {
      this.editorLoader = true;

      try {
        await this.onAutoSaveDraft();
      } finally {
        this.editorLoader = false;
      }
    }, // onAutoSaveWithLoader()

    async onAutoSaveDraft() {
      if (!this.autoSave || this.isSaving || !this.isNotesChanged) {
        return;
      }

      await this.processSavingNotesImagesInStorage({
        sessionActivityId: this.sessionActivity.id,
      });
      this.saveSessionNotes({
        notes: this.editNotes,
      });
    }, // onAutoSaveDraft()

    async onSaveDraft() {
      const updateEditNotes = await this.processSavingNotesImagesInStorage({
        originalNotes: this.sessionActivity.notes,
        updatedNotes: this.editNotes,
        sessionActivityId: this.sessionActivity.id,
        draftSave: true,
        hardUpdate: true,
      });
      this.clearChangesHistory();

      this.saveSessionNotes({
        notes: updateEditNotes,
      });
    }, // onSaveDraft()

    async onSaveChanges() {
      if (!this.validateNoteContent(this.editNotes)) {
        return;
      }

      this.editNotes = await this.processSavingNotesImagesInStorage({
        originalNotes: this.sessionActivity.notes,
        updatedNotes: this.editNotes,
        sessionActivityId: this.sessionActivity.id,
      });
      this.clearChangesHistory();

      this.closeSessionNotes({
        notes: this.editNotes,
        status: 'closed',
      });
    }, // onSaveChanges()

    onCompleteSessionNoteTemplate() {
      const initialField = this.isBackupDifferent && (this.session !== null) && !this.offlineMode
        ? this.activityNoteFieldWithDifference
        : this.sessionActivity?.sessionNotesCurrentField || null;

      this.applySessionNoteTemplate(
        this.sessionNoteTemplate,
        initialField,
        get(this.sessionActivity, 'notes', ''),
        '',
      );
    }, // onCompleteSessionNoteTemplate()

    async onSelectNoteTemplate(sessionNoteTemplateId) {
      const sessionNoteTemplate = this.findSessionNoteTemplate(sessionNoteTemplateId);
      const originalNotes = get(this.sessionActivity, 'notes', '');
      // Picking a template while the session is still running is not note completion, so don't
      // prompt to link an appointment then. The prompt still fires on the completion paths.
      const skipAppointmentPrompt = get(this.sessionActivity, 'status') === 'in-progress';

      if (this.offlineMode && sessionNoteTemplate) {
        if (this.hasStaffMemberField(sessionNoteTemplate)) {
          this.notifyWarning(this.$t('sessions.noteTemplate.staffMemberFieldNotAvailableOffline'));
          return;
        }
      }

      if (this.editNotes) {
        const message = sessionNoteTemplateId
          ? this.$t('run.done.notifySessionNoteTemplateConfirm')
          : this.$t('run.done.notifySessionNoteTemplateClear');
        this.$q
          .dialog({
            title: this.$t('common.dialogTitleConfirm'),
            message,
            cancel: true,
            persistent: true,
          })
          .onOk(async () => {
            this.saveSessionNotes({
              sessionNoteTemplateId,
              notes: '',
            });

            await this.applySessionNoteTemplate(
              sessionNoteTemplate,
              null,
              '',
              originalNotes,
              { skipAppointmentPrompt },
            );
          });
      } else {
        this.saveSessionNotes({
          sessionNoteTemplateId,
          notes: '',
        });
        await this.applySessionNoteTemplate(sessionNoteTemplate, null, '', originalNotes, { skipAppointmentPrompt });
      }
    }, // onSelectNoteTemplate()

    onSwitchNotesView() {
      this.showBackupNotes = !this.showBackupNotes;
      this.$emit('show-backup-notes', this.showBackupNotes);
    }, // onSwitchNotesView()

    onDismiss() {
      this.saveSessionNotes({ notes: this.sessionActivity.notes });
    }, // onDismiss()

    async onRestoreBackupNotes() {
      if (this.session === null) {
        return;
      }

      const notesBackup = this.getBackupNotes(this.sessionActivity.id);

      this.saveSessionNotes({
        notes: notesBackup.notes,
      });
      this.$emit('show-backup-notes', false);
    }, // onRestoreBackupNotes()

    showRequestSignatureDialog() {
      // contains the current user to skip him in the signature request
      const skipUsers = chain(this.sessionActivity)
        .get('signatures')
        .map('userId')
        .concat(chain(this.sessionActivity).get('requiredSignatories', []).map('userId').value())
        .concat([this.userDetails.userId])
        .value();

      this.openComponentDialog(
        RequestSignature,
        {
          sessionId: get(this.session, 'id', null),
          clientId: get(this.client, 'id', null),
          skipUsers,
          signatoryOptions: this.getSignatoryOptions(null),
        },
        async ({ selectedUser, signatory }) => {
          try {
            this.$q.loading.show();

            const userId = signatory?.userId || selectedUser?.user || null;
            const userName = signatory?.userName || selectedUser?.userName || null;

            const requiredSignatory = {
              uid: uid(),
              userId,
              userName,
              label: signatory?.label || '',
              ...(signatory?.value && { clientDataField: signatory.value }),
              completed: false,
            };

            if (userId) {
              const staffInfo = await this.getStaffInfoForSignature(
                userName,
                userId,
                this.sessionNoteTemplate?.includeProfessionalCredentials,
              );

              Object.assign(requiredSignatory, staffInfo);
            }

            this.requestSignature(this.session, requiredSignatory, this.sessionActivity);

            this.$emit('request-signature', requiredSignatory);
          } finally {
            this.$q.loading.hide();
          }
        },
      );
    }, // showRequestSignatureDialog()

    async showSignatureDialog(anonymous, userId, staffInfo, label = '', clientDataFieldId = null) {
      this.refreshGeolocation(false);

      this.openComponentDialog(
        Signature,
        {
          anonymous,
          userName: staffInfo.userName,
          includeGeolocation: true,
          dialogTitle: label,
          signatoryOptions: (anonymous || clientDataFieldId) ? this.getSignatoryOptions(clientDataFieldId) : [],
          initialSignatory: clientDataFieldId,
          sessionActivity: this.sessionActivity,
          requestedChanges: this.requestedChanges,
          reason: this.sessionActivity.changeRequest?.reason,
          changeRequestStatus: this.sessionActivity.changeRequest?.status,
          userId,
        },
        async (signatureInfo) => {
          const signatureDetails = {
            userName: signatureInfo.userName || null,
            userId: signatureInfo.signatory?.userId || userId,
            label: signatureInfo.signatory?.label || '',
            ...(signatureInfo.signatory?.value && { clientDataField: signatureInfo.signatory.value }),
            ...(signatureInfo.geolocation && { geolocation: signatureInfo.geolocation }),
            ...(staffInfo.professionalCredentials && {
              professionalCredentials: staffInfo.professionalCredentials,
            }),
          };

          this.saveSignature(
            this.sessionActivity,
            signatureInfo.data,
            signatureDetails,
            (sessionActivityChanges, sessionActivty, signature) => {
              this.$emit('add-signature', signature);
            },
          );
        },
      );
    }, // showSignatureDialog()

    getSessionActivityChanges(sessionNoteFields = {}) {
      const { updateSessionActivity } = this.getUpdatedSessionNoteFields(this.sessionActivity, sessionNoteFields);

      return { updateData: updateSessionActivity || {} };
    }, // getSessionActivityChanges()

    updateSessionNoteFieldPromptParams(updateObj) {
      if (this.sessionNoteFieldPrompt?.update && typeof this.sessionNoteFieldPrompt.update === 'function') {
        this.sessionNoteFieldPrompt.update(updateObj);
      }
    },

    /**
     * @param {Object} sessionNoteTemplate
     * @param {string | null} initialField
     * @param {string} initialSessionNotes
     * @param {string} [originalNotes]
     * @return {Promise<void>}
     */
    async shouldPromptAppointmentLink(sessionActivity) {
      if (!this.connectedClinic?.integration || sessionActivity?.appointment) {
        return false;
      }
      const clientId = sessionActivity?.client;
      if (!clientId) {
        return false;
      }
      // Resolve the activity's client to determine whether this is a staff activity.
      // getSessionClient only reads in-memory state (currentClient / loaded clients), which
      // often doesn't hold staff records at note-completion time, so fall back to a DB read.
      let activityClient = this.getSessionClient(clientId);
      if (!activityClient) {
        try {
          const clientsDb = new ClientsDB();
          activityClient = await clientsDb.read(clientId);
        } catch (err) {
          logEvent(err);
        }
      }
      // Fail safe: if the client can't be resolved, don't prompt — avoids showing the modal
      // for staff-only activities (which must never trigger it).
      if (!activityClient) {
        return false;
      }
      return activityClient.type !== 'staff';
    }, // shouldPromptAppointmentLink()

    /**
     * The same-day appointments the activity could be linked to. Used to decide whether to open
     * the end-of-session modal at all (we don't open it with nothing to link) and then handed to
     * the modal so it doesn't have to query again.
     *
     * @param {Object} sessionActivity
     * @return {Promise<Object[]>}
     */
    loadLinkableAppointmentsForActivity(sessionActivity) {
      return this.loadLinkableAppointments({
        clinic: this.userDetails.clinic,
        integration: this.connectedClinic.integration,
        clientId: sessionActivity.client,
        userId: sessionActivity.user,
        startDate: this.appointmentLookupStartDate(sessionActivity),
      });
    }, // loadLinkableAppointmentsForActivity()

    promptAppointmentLinkBeforeNoteCompletion(sessionActivity, preloadedAppointments) {
      return new Promise((resolve, reject) => {
        this.openComponentDialog(
          ChangeSessionActivityAppointment,
          {
            appointmentId: '',
            clientId: sessionActivity.client,
            userId: sessionActivity.user,
            startDate: this.appointmentLookupStartDate(sessionActivity),
            allowUnlink: false,
            linkPromptMode: true,
            preloadedAppointments,
          },
          async (newAppointmentId) => {
            try {
              const changes = newAppointmentId
                ? await this.linkAppointmentToSessionActivity(sessionActivity, newAppointmentId, {
                  sessionId: this.session?.id ?? null,
                })
                : null;
              resolve(changes);
            } catch (err) {
              reject(err);
            }
          },
          () => resolve(null),
        );
      });
    }, // promptAppointmentLinkBeforeNoteCompletion()

    async applySessionNoteTemplate(
      sessionNoteTemplate,
      initialField,
      initialSessionNotes,
      originalNotes = '',
      { skipAppointmentPrompt = false } = {},
    ) {
      // If the default session note template was deleted and the user selects another one,
      // emit an event to the CollectData component to use a new template
      this.$emit('has-note-template', { hasNoteTemplate: !!sessionNoteTemplate });

      if (!sessionNoteTemplate) {
        return;
      }

      let linkedAppointmentChanges = null;
      if (!skipAppointmentPrompt && await this.shouldPromptAppointmentLink(this.sessionActivity)) {
        let linkableAppointments = [];
        try {
          linkableAppointments = await this.loadLinkableAppointmentsForActivity(this.sessionActivity);
        } catch (err) {
          logEvent(err);
        }
        // Only prompt when there's at least one appointment to link to; otherwise continue straight
        // to the note flow rather than showing an empty modal.
        if (!isEmpty(linkableAppointments)) {
          try {
            linkedAppointmentChanges = await this.promptAppointmentLinkBeforeNoteCompletion(
              this.sessionActivity,
              linkableAppointments,
            );
          } catch (err) {
            this.notifyNegative(err.message);
            logEvent(err);
            return;
          }
        }
      }
      const effectiveLinkedAppointment = linkedAppointmentChanges?.updatedAppointment || this.linkedAppointment;

      const templateText = get(sessionNoteTemplate, 'templateText', '');
      const templateFields = this.fetchTemplateFields(templateText);
      const promptedFields = this.filterPromptedTemplateFields(templateFields);
      this.alwaysShowNotesEditor = isEmpty(promptedFields);

      await this.updateSessionNoteFieldData(templateFields, effectiveLinkedAppointment);

      let staffProfile = null;

      if (sessionNoteTemplate?.additionalSignatories?.some(({ context }) => context === 'staffMember')) {
        try {
          staffProfile = await this.getStaffMemberByUser(this.sessionActivity?.user);
        } catch (err) {
          logEvent(err);
        }
      }

      this.sessionNoteFieldPrompt = this.openComponentDialog(
        SessionNoteFieldPrompt,
        {
          sessionNoteTemplate,
          templateFields,
          sessionNoteFieldData: this.sessionNoteFieldData,
          initialField,
          initialSessionNotes,
          originalNotes,

          session: this.session,
          sessionActivity: this.sessionActivity,
          linkedAppointment: effectiveLinkedAppointment,
          allPrograms: this.allPrograms,

          client: this.client,
          staffProfile,

          alwaysAllowNoteCompletion: this.sessionActivity.type === 'form',
        },
        async ({ cancel, sessionActivityChanges, keepDialogOpen, saveToBackupOnly = false, sendToIntegration }) => {
          if (!keepDialogOpen) {
            this.sessionNoteFieldPrompt = null;
          }

          if (cancel) {
            this.saveSessionNotes(
              sessionActivityChanges,
              saveToBackupOnly,
              linkedAppointmentChanges?.sessionActivityChanges,
            );
            return;
          }
          // Save changes to start/end timestamp if they are changed
          const { updateData } = this.getSessionActivityChanges(sessionActivityChanges?.sessionNotesFieldData);

          this.closeSessionNotes(
            { ...sessionActivityChanges, ...updateData },
            sessionNoteTemplate,
            sendToIntegration,
            linkedAppointmentChanges?.sessionActivityChanges,
          );
        },
        null,
        null,
        { persistent: true },
      );
    }, // applySessionNoteTemplate()

    async saveSessionNotes(changes, saveToBackupOnly = false, overrideAppointmentData = null) {
      const sessionActivityChanges = { ...changes };

      if (this.offlineMode
        && sessionActivityChanges.notes
        && sessionActivityChanges.notes !== this.sessionActivity?.notes
      ) {
        sessionActivityChanges.notes = await replaceLocalImagesWithStorage(changes.notes);
      }

      this.$emit('save', sessionActivityChanges, saveToBackupOnly, false, overrideAppointmentData);
    }, // saveSessionNotes()

    async closeSessionNotes(changes, sessionNoteTemplate, sendToIntegration = false, overrideAppointmentData = null) {
      const dataToEmit = {
        ...changes,
      };

      if (this.offlineMode && dataToEmit.notes) {
        dataToEmit.notes = await replaceLocalImagesWithStorage(dataToEmit.notes);
      }

      this.$emit('save', dataToEmit, false, sendToIntegration, overrideAppointmentData);
    }, // closeSessionNotes()

    /**
     * Load session note template by id from session activity
     * display notification if it does not exist
     */
    async loadExistingSessionNoteTemplate() {
      if (this.sessionNoteTemplateId) {
        this.isLoadingTemplates = true;

        try {
          const sessionNoteTemplatesDb = new SessionNoteTemplatesDB();
          this.existingSessionNoteTemplate = await sessionNoteTemplatesDb
            .readNoteTemplateIfExists(this.sessionNoteTemplateId);
          if (this.existingSessionNoteTemplate) {
            const templateText = get(this.existingSessionNoteTemplate, 'templateText', '');

            const templateFields = this.fetchTemplateFields(templateText);
            const promptedFields = this.filterPromptedTemplateFields(templateFields);
            this.alwaysShowNotesEditor = isEmpty(promptedFields);
          }
        } catch (err) {
          if (this.canCompleteSessionNoteTemplate) {
            // If the default session note template was deleted and the user selects another one,
            // emit an event to the CollectData component
            this.$emit('has-note-template', { hasNoteTemplate: false });
            this.notifyNegative(
              this.networkOnline
                ? this.$t('sessions.noteTemplate.activeNoteTemplateDeletedMessage')
                : this.$t('run.collectData.badConnectionSelectTemplate'),
            );
          }
        } finally {
          this.isLoadingTemplates = false;
        }
      }
    }, // loadExistingSessionNoteTemplate()

    async loadCurrentSessionNoteTemplate() {
      if (this.isParent && !this.sessionNoteTemplateId) {
        return;
      }

      this.isLoadingTemplates = true;
      try {
        // Load at least one note template to check if a clinic has templates at all
        if (this.sessionNoteTemplateId) {
          await this.loadExistingSessionNoteTemplate();
          await this.loadClinicUnits();

          if (this.existingSessionNoteTemplate) {
            this.sessionNoteTemplates = [this.existingSessionNoteTemplate];

            if (
              this.applyImmediately
              && (get(this.sessionActivity, 'status') === 'finished')
              && (!get(this.sessionActivity, 'notes'))
            ) {
              await this.applySessionNoteTemplate(
                this.existingSessionNoteTemplate,
                get(this.sessionActivity, 'sessionNotesCurrentField', null),
                get(this.sessionActivity, 'notes', ''),
              );
            }
          }
        } else {
          await this.loadAllTemplates();
        }
      } finally {
        this.isLoadingTemplates = false;
      }
    }, // loadCurrentSessionNoteTemplate()

    async loadAllTemplates(val, update) {
      if (this.isParent && !this.sessionNoteTemplateId) {
        // Currently we don't show templates to parents
        update();
        return;
      }

      if (size(this.sessionNoteTemplates) > 1) {
        // Already loaded
        update();
        return;
      }

      const sessionNoteTemplatesDb = new SessionNoteTemplatesDB();
      const sessionNoteTemplates = await sessionNoteTemplatesDb.readAll(
        [
          ['clinic', '==', this.userDetails.clinic],
        ],
      );

      let templates = [];

      // Only do this check if we are offline
      if (this.offlineMode) {
        templates = sessionNoteTemplates.map((template) => {
          template.isAvailableOffline = !this.hasStaffMemberField(template);
          return template;
        });
      } else {
        templates = sessionNoteTemplates;
      }

      this.sessionNoteTemplates = sortBy(templates, 'noteTemplateName');
      if (isFunction(update)) {
        update(() => { });
      }
    }, // loadAllTemplates()

    findSessionNoteTemplate(sessionNoteTemplateId) {
      return find(this.sessionNoteTemplates, { id: sessionNoteTemplateId });
    }, // findSessionNoteTemplate()

    clearChangesHistory() {
      if (this.$refs.editor) {
        this.$refs.editor.clearChangesHistory();
      }
    }, // clearChangesHistory()

    getSignatoryOptions(clientDataFieldId) {
      const mergedCustomData = { ...this.client?.customData, ...this.staffProfile?.customData };
      const mergedDataFields = [...this.clientDataFields, ...this.staffMemberDataFields];

      return chain(mergedDataFields)
        .filter({ type: 'user' })
        .map((clientDataField) => ({
          value: clientDataField.id,
          label: clientDataField.label,
          userId: mergedCustomData[clientDataField.id]?.user || '',
          userName: mergedCustomData[clientDataField.id]?.userName || '',
        }))
        .reject((signatoryOption) => (
          (signatoryOption.value !== clientDataFieldId)
            && chain(this.sessionActivity)
              .get('signatures')
              .map('clientDataField')
              .concat(chain(this.sessionActivity)
                .get('requiredSignatories')
                .map('clientDataField')
                .value())
              .includes(signatoryOption.value)
              .value()
        ))
        .sortBy('label')
        .value();
    }, // getSignatoryOptions()

    hasStaffMemberField(template) {
      const templateFields = this.fetchTemplateFields(get(template, 'templateText', ''));
      return this.templateFieldsHasGroup(templateFields, 'staffMember') || false;
    }, // hasStaffMemberField()

    scrollToRequestBanner() {
      const bannerElement = this.$refs.changeRequestBanner;
      if (bannerElement) {
        const scrollableParent = bannerElement.closest('#session-card-dialog');
        if (!scrollableParent) {
          this.$refs.changeRequestBanner.scrollIntoView({ behavior: 'smooth' });
          return;
        }

        this.$emit('scroll-to-banner', bannerElement);
      }
    }, // scrollToRequestBanner

    scrollToAddendums() {
      const addendumsElement = this.$refs.addendumCards;
      if (addendumsElement) {
        const scrollableParent = addendumsElement.closest('#session-card-dialog');
        if (!scrollableParent) {
          addendumsElement.scrollIntoView({ behavior: 'smooth' });
          return;
        }

        this.$emit('scroll-to-banner', addendumsElement);
      }
    }, // scrollToAddendums()

    async onApproveChangeRequest() {
      const sessionNotesFieldData = {
        ...this.sessionActivity.sessionNotesFieldData,
      };

      const mappedFieldData = this.mapChangeRequestData(this.requestedChanges);

      sessionNotesFieldData.session = {
        ...sessionNotesFieldData.session,
        ...mappedFieldData,
      };

      const status = this.requiredSignatories?.length > 0 ? 'pending-signatures' : 'closed';

      const changeRequestKeys = Object.keys(this.requestedChanges);

      const { updatedSessionNoteFields, updateSessionActivity } = this.getUpdatedSessionNoteFields(
        this.sessionActivity,
        this.getFieldDataForApproval(sessionNotesFieldData),
      );

      if (updatedSessionNoteFields.session?.sessionDuration) {
        changeRequestKeys.push('sessionDuration');
      }

      const notes = this.getRenderedNotesWithChangeRequest(
        this.sessionActivity,
        updatedSessionNoteFields,
        changeRequestKeys,
      );

      const sessionActivityChanges = {
        status,
        sessionNotesFieldData,
        notes,
        ...updateSessionActivity,
        changeRequest: {
          ...this.sessionActivity.changeRequest,
          status: 'approved',
        },
      };

      if (this.sessionActivity.changeRequest.rejectionReason) {
        sessionActivityChanges.changeRequest.rejectionReason = null;
      }

      this.editNotes = notes;

      this.saveSessionNotes(sessionActivityChanges);
    }, // onApproveChangeRequest

    onRejectChangeRequest() {
      this.openComponentDialog(
        ChangeRequestRejectModal,
        {},
        async (changedRequestChanges) => {
          const sessionActivityChanges = {
            changeRequest: {
              ...this.sessionActivity.changeRequest,
              ...changedRequestChanges,
            },
          };

          this.saveSessionNotes(sessionActivityChanges);
        },
        null,
        null,
        { persistent: true },
      );
    }, // onRejectChangeRequest

    async onDeleteRejectChangeRequest() {
      const sessionActivityChanges = {
        changeRequest: {
          ...this.sessionActivity.changeRequest,
          status: 'pending',
          rejectionReason: null,
        },
      };

      this.saveSessionNotes(sessionActivityChanges);
    }, // onDeleteRejectChangeRequest

    onCreateChangeRequest() {
      this.openComponentDialog(
        SessionActivityChangeRequest,
        {
          sessionNoteFields: this.sessionNoteFields,
          sessionActivity: this.sessionActivity,
          newRequestAfterRejection: true,
        },
        (changedRequestChanges) => {
          const sessionActivityChanges = {
            changeRequest: {
              ...changedRequestChanges,
            },
          };

          this.saveSessionNotes(sessionActivityChanges);
        },
        null,
        null,
        { persistent: true },
      );
    }, // onCreateChangeRequest
  },
};
</script>

<style lang="scss">
.wrapper {
  position: relative;
}

.autosave-status {
  position: absolute;
  right: 40px;
  top: 12px;

  color: $green;
  &--saving {
    color: $grey-6;
  }
}

.autosave-icon {
  position: absolute;
  right: -100px;
  top: 9px;

  color: $green;
  &--saving {
    color: $grey-6;
  }
}

@media (max-width: 730px) {
  .autosave-icon {
    top: 43px;
  }
}
</style>
